import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Checkbox,
  ListItemText,
  Slider,
  Typography,
  Box,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';

export default function DynamicFilter({
  label,
  options = [],
  value,
  onChange,
  multiple = false,
  type = "select",
  min = 0,
  max = 100,
  step = 1,
  updateQSParameter,
  maxDate = null
}) {
  
  // Determine if filter has a value
  const hasValue = () => {
    if (type === 'date') {
      return value !== null && value !== undefined;
    }
    if (type === 'slider') {
      return value > min;
    }
    if (multiple) {
      return Array.isArray(value) && value.length > 0;
    }
    return value !== '' && value !== null && value !== undefined;
  };

  // Get label color based on whether filter has value
  const getLabelColor = () => {
    return hasValue() ? '#59e200' : '#fff';
  };

  // Helper function to ensure we have proper arrays for multiple selects
  const normalizeValue = (val) => {
    if (!multiple) return val;
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(v => v !== 'ALL_VALUES' && v !== '');
    return [val].filter(v => v !== 'ALL_VALUES' && v !== '');
  };

  // Get the current normalized value
  const currentValue = normalizeValue(value);

  // Determine height based on filter type and label
  const getControlHeight = () => {
    if (type === 'date') {
      return '12px';
    }
    if (type === 'autocomplete' && (label === 'Hashtag' || label === 'Subcategory')) {
      return multiple ? 'auto' : 25;
    }
    return multiple ? 'auto' : 30;
  };

  const getControlMinHeight = () => {
    if (type === 'date') {
      return '12px';
    }
    if (type === 'autocomplete' && (label === 'Hashtag' || label === 'Subcategory')) {
      return 25;
    }
    return 30;
  };

  return (
    <FormControl
      sx={{
        mb: 0, // Remove margin bottom since parent handles spacing
        width: '100%',
        fontFamily: 'Oswald, sans-serif',
      }}
    >

      {type === 'autocomplete' && (
        <Autocomplete
          multiple={multiple}
          options={options}
          getOptionLabel={(option) => option.label || option.value || ''}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={
            multiple 
              ? options.filter((o) => currentValue.includes(o.value))
              : options.find((o) => o.value === currentValue) || null
          }
          onChange={(event, newValue) => {
            let syntheticEvent;
            if (multiple) {
              const selectedValues = Array.isArray(newValue) ? newValue.map(item => item.value).filter(v => v && v !== 'ALL_VALUES') : [];
              syntheticEvent = {
                target: {
                  value: selectedValues,
                },
              };
            } else {
              syntheticEvent = {
                target: {
                  value: newValue ? newValue.value : '',
                },
              };
            }
            onChange(syntheticEvent);
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.label}
                size="small"
                {...getTagProps({ index })}
                key={option.value}
                sx={{
                  color: '#fff',
                  borderColor: '#404040',
                  backgroundColor: '#333',
                  fontSize: '10px',
                  height: '12px',
                  '& .MuiChip-label': {
                    fontSize: '10px',
                    fontFamily: 'Oswald, sans-serif',
                  },
                  '& .MuiChip-deleteIcon': {
                    color: '#ccc',
                    fontSize: '14px',
                  },
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              fullWidth
              sx={{
                backgroundColor: '#222',
                fontFamily: 'Oswald, sans-serif',
                  '& .MuiInputLabel-root': {
                    color: `${getLabelColor()} !important`,
                    fontSize: '13px',
                    fontFamily: 'Oswald, sans-serif',
                    transform: 'translate(14px, 7px) scale(1)',
                    transition: 'color 0.2s ease',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                      color: `${getLabelColor()} !important`,
                    },
                    '&.Mui-focused': {
                      color: `${getLabelColor()} !important`,
                    },
                  },
                '& .MuiOutlinedInput-root': {
                  height: getControlHeight(),
                  minHeight: getControlMinHeight(),
                  fontSize: '13px !important',
                  color: '#fff !important',
                  fontFamily: 'Oswald, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  '& fieldset': { borderColor: '#404040' },
                  '&:hover fieldset': { borderColor: '#777' },
                  '&.Mui-focused fieldset': { borderColor: '#aaa' },
                },
                  '& .MuiInputBase-input': {
                    color: '#fff !important',
                    WebkitTextFillColor: '#fff !important',
                    padding: '0 !important',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiSvgIcon-root': { color: '#ccc' },
              }}
            />
          )}
          sx={{
            '& .MuiAutocomplete-inputRoot': {
              padding: multiple ? '4px 39px 4px 6px !important' : '4px 39px 4px 6px !important',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiAutocomplete-endAdornment': {
              top: '50%',
              transform: 'translateY(-50%)',
              right: '9px',
            },
            '& .MuiAutocomplete-popupIndicator': {
              color: '#ccc',
            },
            '& .MuiAutocomplete-clearIndicator': {
              color: '#ccc',
            },
          }}
          ListboxProps={{
            sx: {
              backgroundColor: '#111',
              color: '#fff',
              fontSize: '13px',
              fontFamily: 'Oswald, sans-serif',
              '& .MuiAutocomplete-option': {
                color: '#fff',
                fontSize: '13px',
                fontFamily: 'Oswald, sans-serif',
                padding: '2px 16px',
                minHeight: '30px',
                '&.Mui-focused': {
                  backgroundColor: '#333',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: '#444',
                },
              },
            },
          }}
        />
      )}

      {/* Select dropdown or Autocomplete */}
      {type === 'select' && (
        <>
          {/* If multi-select or short list, use Select with consistent styling */}
          {!(multiple === false && options.length > 1000) ? (
            <>
              <InputLabel
                id={`${label}-label`}
                sx={{
                  color: `${getLabelColor()} !important`,
                  fontSize: '13px',
                  fontFamily: 'Oswald, sans-serif',
                  transform: 'translate(14px, 7px) scale(1)',
                  transition: 'color 0.2s ease',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                    color: `${getLabelColor()} !important`,
                  },
                  '&.Mui-focused': { 
                    color: `${getLabelColor()} !important` 
                  },
                }}
              >
                {label}
              </InputLabel>

              <Select
                labelId={`${label}-label`}
                id={`${label}-select`}
                multiple={multiple}
                value={multiple ? currentValue : (currentValue || '')}
                onChange={(event) => {
                  // Ensure we handle the onChange correctly for multiple selects
                  let newValue = event.target.value;
                  
                  if (multiple) {
                    // For multiple selects, ensure we have an array
                    if (typeof newValue === 'string') {
                      newValue = newValue.split(',');
                    }
                    // Filter out any empty values or ALL_VALUES
                    newValue = Array.isArray(newValue) ? newValue.filter(v => v && v !== 'ALL_VALUES') : [];
                  }
                  
                  // Create synthetic event
                  const syntheticEvent = {
                    target: {
                      value: newValue
                    }
                  };
                  
                  onChange(syntheticEvent);
                }}
                input={<OutlinedInput label={label} />}
                displayEmpty={!multiple}
                renderValue={(selected) => {
                  if (multiple) {
                    if (!Array.isArray(selected) || selected.length === 0) return '';
                    return selected.map(val =>
                      options.find(o => o.value === val)?.label || val
                    ).join(', ');
                  } else {
                    // Single select
                    if (!selected || selected === '') return '';
                    const foundOption = options.find(o => o.value === selected);
                    return foundOption?.label || selected || '';
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#111',
                      color: '#fff',
                      fontSize: '13px',
                      fontFamily: 'Oswald, sans-serif',
                      '& .MuiMenuItem-root': { 
                        color: '#fff',
                        fontSize: '13px',
                        fontFamily: 'Oswald, sans-serif',
                        padding: '2px 16px',
                        minHeight: '30px',
                        '&.Mui-focused': {
                          backgroundColor: '#333',
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: '#444',
                        },
                      },
                      '& .MuiCheckbox-root': { color: '#bbb', p: 0.5 },
                      '& .Mui-selected': { backgroundColor: '#444' },
                    },
                  },
                }}
                sx={{
                  backgroundColor: '#222',
                  color: '#fff',
                  fontSize: '13px',
                  height: 30,
                  fontFamily: 'Oswald, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#404040' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#777' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#aaa' },
                  '.MuiSvgIcon-root': { color: '#ccc' },
                  '& .MuiSelect-select': {
                    padding: '4px 32px 4px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#fff !important',
                  },
                  '& .MuiSelect-select:focus': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {multiple && (
                      <Checkbox
                        checked={currentValue.includes(option.value)}
                        sx={{
                          color: '#fff',
                          p: 0.5,
                          fontSize: '0.75rem',
                          '&.Mui-checked': { color: '#fff' },
                        }}
                      />
                    )}
                    <ListItemText
                      primary={option.label}
                      sx={{
                        fontFamily: 'Oswald, sans-serif',
                        fontSize: '0.75rem',
                        color: '#fff',
                        '& .MuiTypography-root': {
                          fontFamily: 'Oswald, sans-serif',
                          fontSize: '13px',
                        },
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </>
          ) : (
            // Autocomplete for large, single-select dropdowns
            <Autocomplete
              disablePortal
              options={options}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={options.find((o) => o.value === currentValue) || null}
              onChange={(event, newValue) => {
                const syntheticEvent = {
                  target: {
                    value: newValue?.value || '',
                  },
                };
                onChange(syntheticEvent);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  variant="outlined"
                  fullWidth
                  sx={{
                    backgroundColor: '#222',
                    fontFamily: 'Oswald, sans-serif',
                      '& .MuiInputLabel-root': {
                        color: `${getLabelColor()} !important`,
                        fontSize: '13px',
                        fontFamily: 'Oswald, sans-serif',
                        transform: 'translate(14px, 6px) scale(1)', // Adjusted for 30px height
                        transition: 'color 0.2s ease',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)',
                          color: `${getLabelColor()} !important`,
                        },
                        '&.Mui-focused': {
                          color: `${getLabelColor()} !important`,
                        },
                      },
                    '& .MuiOutlinedInput-root': {
                      height: 30, // Standard 30px height
                      fontSize: '13px',
                      color: '#fff',
                      fontFamily: 'Oswald, sans-serif',
                      '& fieldset': { borderColor: '#404040' },
                      '&:hover fieldset': { borderColor: '#777' },
                      '&.Mui-focused fieldset': { borderColor: '#aaa' },
                    },
                    '& .MuiInputBase-input': {
                    color: '#fff !important',
                    WebkitTextFillColor: '#fff !important',
                  },
                    '& .MuiSvgIcon-root': { color: '#ccc' },
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  padding: '0px !important',
                  height: '30px !important',
                },
                '& .MuiAutocomplete-endAdornment': {
                  top: '50%',
                  transform: 'translateY(-50%)',
                },
                '& .MuiAutocomplete-popupIndicator': {
                  color: '#ccc',
                },
                '& .MuiAutocomplete-clearIndicator': {
                  color: '#ccc',
                },
              }}
              ListboxProps={{
                sx: {
                  backgroundColor: '#111',
                  color: '#fff',
                  fontSize: '13px',
                  fontFamily: 'Oswald, sans-serif',
                  '& .MuiAutocomplete-option': {
                    color: '#fff',
                    fontSize: '13px',
                    fontFamily: 'Oswald, sans-serif',
                    padding: '2px 16px',
                    minHeight: '30px',
                    '&.Mui-focused': {
                      backgroundColor: '#333',
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: '#444',
                    },
                  },
                },
              }}
            />
          )}
        </>
      )}

      {/* Single Date picker control - ENHANCED with yyyy-mm-dd format and reduced height */}
      {type === 'date' && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={label}
            format="yyyy-MM-dd"
            value={value ? dayjs(value).toDate() : null}
            onChange={(newDate) => {
              if (newDate) {
                const dayjsDate = dayjs(newDate);
                onChange(dayjsDate);
              } else {
                onChange(null);
              }
            }}
            maxDate={maxDate ? dayjs(maxDate).toDate() : null}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                size: "small",
                sx: {
                  backgroundColor: '#222',
                  fontFamily: 'Oswald, sans-serif',
                  
                  // CRITICAL: Force white text color in ALL states
                  "& .MuiInputBase-input": {
                    color: '#fff !important',
                    WebkitTextFillColor: '#fff !important',
                  },
                  
                  // Force white for input element specifically
                  "& input": {
                    color: '#fff !important',
                    WebkitTextFillColor: '#fff !important',
                  },
                  
                  // Force white when value is selected/not empty
                  "& input:not(:placeholder-shown)": {
                    color: '#fff !important',
                    WebkitTextFillColor: '#fff !important',
                  },

                  '& .MuiInputLabel-root': {
                    color: `${getLabelColor()} !important`,
                    fontSize: '13px !important',
                    fontFamily: 'Oswald, sans-serif !important',
                    transform: 'translate(14px, -1px) scale(1)',
                    transition: 'color 0.2s ease',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                      color: `${getLabelColor()} !important`,
                    },
                    '&.Mui-focused': {
                      color: `${getLabelColor()} !important`,
                    },
                  },

                  '& .MuiOutlinedInput-root': {
                    height: '12px !important',
                    minHeight: '12px !important',
                    maxHeight: '12px !important',
                    fontSize: '13px !important',
                    fontFamily: 'Oswald, sans-serif !important',
                    color: '#fff !important',
                    
                    '& fieldset': { 
                      borderColor: '#404040 !important',
                    },
                    '&:hover fieldset': { 
                      borderColor: '#777 !important',
                    },
                    '&.Mui-focused fieldset': { 
                      borderColor: '#aaa !important',
                    },
                    
                    '& input': {
                      color: '#fff !important',
                      fontSize: '13px !important',
                      fontFamily: 'Oswald, sans-serif !important',
                      WebkitTextFillColor: '#fff !important',
                      caretColor: '#fff !important',
                      height: '12px !important',
                      lineHeight: '12px !important',
                      padding: '0px 14px !important',
                      margin: '0 !important',
                      border: 'none !important',
                      outline: 'none !important',
                      backgroundColor: 'transparent !important',
                      boxSizing: 'border-box !important',
                      
                      '&::placeholder': {
                        color: '#fff !important',
                        opacity: 0.7,
                        WebkitTextFillColor: '#fff !important',
                      },
                      // ENHANCED: Ensure white text in ALL states to match Location
                      '&:focus, &:hover, &:active, &:not(:focus), &:blur, &[value], &[value=""], &:not([value=""]), &:invalid, &:valid': {
                        color: '#fff !important',
                        WebkitTextFillColor: '#fff !important',
                        backgroundColor: 'transparent !important',
                      },
                      '&:-webkit-autofill': {
                        WebkitTextFillColor: '#fff !important',
                        WebkitBoxShadow: '0 0 0px 1000px #222 inset !important',
                        transition: 'background-color 5000s ease-in-out 0s !important',
                      },
                      // ENHANCED: Force white text on any state change
                      '&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
                        WebkitTextFillColor: '#fff !important',
                        WebkitBoxShadow: '0 0 0px 1000px #222 inset !important',
                      },
                    },
                  },

                  // ENHANCED: Additional overrides to match Location exactly with stronger specificity
                  '& .MuiOutlinedInput-input': {
                    color: '#fff !important',
                    fontSize: '13px !important',
                    fontFamily: 'Oswald, sans-serif !important',
                    WebkitTextFillColor: '#fff !important',
                    caretColor: '#fff !important',
                    height: '12px !important',
                    lineHeight: '12px !important',
                    padding: '0px 14px !important',
                    margin: '0 !important',
                    backgroundColor: 'transparent !important',
                    boxSizing: 'border-box !important',
                    
                    // ENHANCED: Force white text in all states
                    '&:focus, &:hover, &:active, &:not(:focus), &:blur, &[value], &:not([value=""]), &:invalid, &:valid': {
                      color: '#fff !important',
                      WebkitTextFillColor: '#fff !important',
                    },
                  },

                  '& .MuiInputBase-input': {
                    color: '#fff !important',
                    fontSize: '13px !important',
                    fontFamily: 'Oswald, sans-serif !important',
                    WebkitTextFillColor: '#fff !important',
                    caretColor: '#fff !important',
                    height: '12px !important',
                    lineHeight: '12px !important',
                    padding: '0px 14px !important',
                    margin: '0 !important',
                    backgroundColor: 'transparent !important',
                    boxSizing: 'border-box !important',
                    
                    // ENHANCED: Force white text in all states
                    '&:focus, &:hover, &:active, &:not(:focus), &:blur, &[value], &:not([value=""]), &:invalid, &:valid': {
                      color: '#fff !important',
                      WebkitTextFillColor: '#fff !important',
                    },
                  },

                  // ENHANCED: Additional input selectors to ensure white text
                  '& input[type="text"]': {
                    color: '#fff !important',
                    WebkitTextFillColor: '#fff !important',
                    caretColor: '#fff !important',
                  },

                  // Calendar icon styling to match Location dropdown arrow
                  '& .MuiSvgIcon-root': {
                    color: '#ccc !important', // Same icon color as Location
                    fontSize: '18px !important', // Match Location icon size
                  },

                  // Input adornment styling
                  '& .MuiInputAdornment-root': {
                    height: '12px !important',
                    maxHeight: '12px !important',
                    margin: '0 !important',
                  },
                }
              },
              // Calendar popup styling - keep existing black background, white text
              popper: {
                sx: {
                  '& .MuiPaper-root': {
                    backgroundColor: '#000 !important', // Black background for calendar
                    color: '#fff !important', // White text for calendar
                    border: '1px solid #404040 !important',
                  },
                  '& .MuiPickersDay-root': {
                    color: '#fff !important', // White text for dates
                    '&:hover': {
                      backgroundColor: '#333 !important',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#59e200 !important',
                      color: '#000 !important',
                    },
                  },
                  '& .MuiPickersCalendarHeader-root': {
                    color: '#fff !important',
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    color: '#fff !important',
                  },
                  '& .MuiPickersArrowSwitcher-button': {
                    color: '#fff !important',
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    color: '#fff !important',
                  },
                  '& .MuiPickersYear-yearButton': {
                    color: '#fff !important',
                    '&:hover': {
                      backgroundColor: '#333 !important',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#59e200 !important',
                      color: '#000 !important',
                    },
                  },
                  '& .MuiPickersMonth-monthButton': {
                    color: '#fff !important',
                    '&:hover': {
                      backgroundColor: '#333 !important',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#59e200 !important',
                      color: '#000 !important',
                    },
                  },
                }
              }
            }}
          />
        </LocalizationProvider>
      )}

      {/* Slider filter */}
      {type === 'slider' && (
        <>
          <Typography sx={{
            color: getLabelColor(),
            fontFamily: 'Oswald, sans-serif',
            fontSize: '13px',
            mb: 1,
            ml: 1,
          }}>
            {label}: {typeof value === 'number' ? value : ''}
          </Typography>
          <Slider
            value={typeof value === 'number' ? value : min}
            onChange={onChange}
            valueLabelDisplay="off"
            min={min}
            max={max}
            step={step}
            sx={{ color: '#95E655', ml: 1, width: '93%' }}
          />
        </>
      )}
    </FormControl>
  );
}